// Probe evaluation ported from Dealtracker src/appframe/tours/probes.ts (commit 30ca0cb):
// `evaluateCondition` / `evaluateEmpty` / the `Probe` shape are unchanged. The probes are Niobe's:
// the `route` probe matches `parseRoute()` (bid ids are per user, so never a path string), and the
// screen probes read what Niobe screens publish with `useTourState`.
import type { View } from '../App'
import { parseRoute, type Route } from '../lib/router'
import { getTourState } from './useTourState'
import type { ProbeCondition } from './types'

/**
 * What a probe can read: the current route, published tour state and when the tour started.
 * Never the DOM, never the CRDT — booleans and counts only, never bid content.
 */
export interface ProbeContext {
  route: Route
  /** Reads a state published with `useTourState` / `publishTourState`. */
  state: <T = unknown>(key: string) => T | undefined
  /** Epoch ms the tour started (e.g. "a bid created after the tour started"). */
  startedAt: number
}

export interface Probe {
  /** True once the user has done what the step asks. Must not have side effects. */
  test: (args: Record<string, unknown>, ctx: ProbeContext) => boolean
  /** True when the screen shows an empty result set; the step then shows its `emptyHint`. */
  isEmpty?: (args: Record<string, unknown>, ctx: ProbeContext) => boolean
}

// ── Published state ────────────────────────────────────────────────────────────

/** Published by the create-bid flow (HomePage) on success: when, and nothing else (no name). */
export interface BidCreatedTourState {
  /** Epoch ms. */
  at: number
}
export const HOME_BID_CREATED_STATE = 'home.bidCreated'

/** Published by App while it is mounted. */
export interface WorkspaceTourState {
  /** Focus mode is on (`.app.focus`). */
  focusMode: boolean
  /** The chat column is shown (it exists and is not collapsed). */
  chatOpen: boolean
}
export const WORKSPACE_STATE = 'workspace'

/** Published by the modification history window (HistoryModal) while it is open. */
export interface HistoryTourState {
  open: true
}
export const HISTORY_STATE = 'workspace.history'

const workspace = (ctx: ProbeContext) => ctx.state<WorkspaceTourState>(WORKSPACE_STATE)
/** `args.value` (default true) equals `actual`. */
const is = (args: Record<string, unknown>, actual: boolean | undefined): boolean =>
  typeof actual === 'boolean' && actual === (args.value !== false)

/**
 * The probe registry. Probes only read state; they never click, type or write. Every
 * `advanceWhen.probe` of a shipped tour must name one of these (tours.contract.test.ts).
 */
export const PROBES = {
  /**
   * The route matches every given key: `view` (a `View`), `hasBid` (a bid is open), `hasPart`
   * (a part is open in the editor).
   */
  route: {
    test: (args, ctx) => {
      const { view, bid, room } = ctx.route
      if (typeof args.view === 'string' && (args.view as View) !== view) return false
      if (typeof args.hasBid === 'boolean' && args.hasBid !== (bid !== null)) return false
      if (typeof args.hasPart === 'boolean' && args.hasPart !== (room !== null)) return false
      return true
    },
  },
  /** A bid was created after the tour started — the "create your own test bid" step. */
  'home.bidCreated': {
    test: (_args, ctx) => {
      const at = ctx.state<BidCreatedTourState>(HOME_BID_CREATED_STATE)?.at
      return typeof at === 'number' && at >= ctx.startedAt
    },
  },
  /** Focus mode is on — or off, with `{ value: false }`. */
  'workspace.focusMode': {
    test: (args, ctx) => is(args, workspace(ctx)?.focusMode),
  },
  /** The chat column is shown — or hidden, with `{ value: false }`. */
  'workspace.chatOpen': {
    test: (args, ctx) => is(args, workspace(ctx)?.chatOpen),
  },
  /** The modification history window is open — or closed, with `{ value: false }`. */
  'workspace.historyOpen': {
    test: (args, ctx) => is(args, ctx.state<HistoryTourState>(HISTORY_STATE)?.open === true),
  },
} satisfies Record<string, Probe>

export type KnownProbeId = keyof typeof PROBES

export function makeProbeContext(pathname: string, startedAt: number): ProbeContext {
  return { route: parseRoute(pathname), startedAt, state: getTourState }
}

function run(cond: ProbeCondition, ctx: ProbeContext, pick: (p: Probe) => Probe['test'] | undefined): boolean {
  const probe = (PROBES as Record<string, Probe>)[cond.probe]
  const fn = probe && pick(probe)
  if (!fn) return false
  try {
    return fn(cond.args ?? {}, ctx) === true
  } catch {
    // A probe reading a state shape it does not expect must not break the tour.
    return false
  }
}

/** Whether a step's condition holds. An unknown or throwing probe → false. */
export function evaluateCondition(cond: ProbeCondition, ctx: ProbeContext): boolean {
  return run(cond, ctx, (p) => p.test)
}

/** Whether the step's probe reports an empty result set. */
export function evaluateEmpty(cond: ProbeCondition, ctx: ProbeContext): boolean {
  return run(cond, ctx, (p) => p.isEmpty)
}
